import { downloadExcel } from "../../utils/exportExcel";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import { StaticImage } from "../../utils/image";
import { WEB_API_DASHBOARD } from "../../env";
import _ from "lodash";
import ApiLaw from "../../api/law";
import { WatDatePicker } from "thaidatepicker-react";
import moment from "moment";
import ApiMaster from "../../api/master";
import Select from 'react-select';
import SurveyDetailTable from "./SurveyDetailTable";
import DashboardSkeleton from "./DashboardSkeleton";

const DateFilterInput = ({ label, value, onChange }) => (
    <label className="flex flex-col gap-1 text-xs text-slate-500">
        {label}
        <WatDatePicker
            value={value}
            onChange={(christDate) => onChange(christDate ? moment(christDate) : null)}
            inputStyle={{
                backgroundColor: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: 12,
                padding: "8px 12px",
                fontSize: 14,
                width: "100%",
            }}
            placeholder="วว/ดด/ปป"
            dateFormat={"yyyy-MM-dd"}
            displayFormat={"DD/MM/YYYY"}
            clearable={false}
            disabled={false}
            readOnly={false}
            yearBoundary={99}
        />
    </label>
);


const formatNumber = (value) => {
    if (value === null || value === undefined) return "0";
    const num = String(value).replace(/,/g, '');
    if (isNaN(num)) return value;
    return Number(num).toLocaleString('en-US');
};

const normalizeLawHierarchyOrder = (orderId) => {
    const hasOrderId = orderId !== null && orderId !== undefined && String(orderId).trim() !== "";
    const numericOrderId = Number(orderId);
    return hasOrderId && Number.isFinite(numericOrderId)
        ? numericOrderId
        : Number.MAX_SAFE_INTEGER;
};

const compareLawHierarchy = (left, right) => {
    const orderDiff = left.order - right.order;
    if (orderDiff !== 0) return orderDiff;
    return left.label.localeCompare(right.label, "th", { sensitivity: "base" });
};

const monthDefinitions = [
    { value: 10, short: "ต.ค.", long: "ตุลาคม" },
    { value: 11, short: "พ.ย.", long: "พฤศจิกายน" },
    { value: 12, short: "ธ.ค.", long: "ธันวาคม" },
    { value: 1, short: "ม.ค.", long: "มกราคม" },
    { value: 2, short: "ก.พ.", long: "กุมภาพันธ์" },
    { value: 3, short: "มี.ค.", long: "มีนาคม" },
    { value: 4, short: "เม.ย.", long: "เมษายน" },
    { value: 5, short: "พ.ค.", long: "พฤษภาคม" },
    { value: 6, short: "มิ.ย.", long: "มิถุนายน" },
    { value: 7, short: "ก.ค.", long: "กรกฎาคม" },
    { value: 8, short: "ส.ค.", long: "สิงหาคม" },
    { value: 9, short: "ก.ย.", long: "กันยายน" },
];

const getMonthKey = (year, month) => `${Number(year)}-${String(Number(month)).padStart(2, "0")}`;


const DraftlawDashboard = () => {
    const history = useHistory();

    const getDefaultFiscalRange = () => {
        const now = moment();
        const isFiscalYearStart = now.month() >= 9;
        const startYear = isFiscalYearStart ? now.year() : now.year() - 1;
        const endYear = isFiscalYearStart ? now.year() + 1 : now.year();

        return {
            start: moment({ year: startYear, month: 9, date: 1 }),
            end: moment({ year: endYear, month: 8, date: 30 }),
        };
    };

    const defaultRange = getDefaultFiscalRange();
    const [startDate, setStartDate] = useState(defaultRange.start);
    const [endDate, setEndDate] = useState(defaultRange.end);
    const [selectedLawType, setSelectedLawType] = useState(0);
    const [selectedAgency, setSelectedAgency] = useState(0);
    const [selectedMinistry, setSelectedMinistry] = useState(0);
    const [surveyDetailFilter, setSurveyDetailFilter] = useState(null);

    const [agencyOption, setAgencyOption] = useState([
        { value: 0, label: "ทุกหน่วยงาน" },
    ]);
    const [ministryOption, setMinistryOption] = useState([
        { value: 0, label: "ทุกกระทรวง" },
    ]);

    const [lawHierarchyOption, setLawHierarchyOption] = useState([
        { value: 0, label: "ทุกศักดิ์กฎหมาย" },
    ]);

    const [agencyListeningStartEnd, setAgencyListeningStartEnd] = useState([]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const activeRequest = useRef(null);


    const fetchDashboardData = useCallback(async () => {
        activeRequest.current?.abort();
        const controller = new AbortController();
        activeRequest.current = controller;
        setLoading(true);
        setError(null);

        try {
            const payload = {
                start_date: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
                end_date: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
                lawType: selectedLawType ?? "",
                agency_id: selectedAgency ?? 0,
                ministry_id: selectedMinistry ?? 0,
            };

            const response = await fetch(`${WEB_API_DASHBOARD}/dga-user-service-survey/dashboard/hirachy-dashboard`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
                signal: controller.signal,
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            if (controller.signal.aborted) return;
            setAgencyListeningStartEnd(data.agencyListeningStartEnd || []);
        } catch (err) {
            if (controller.signal.aborted) return;
            console.error("Error fetching agency dashboard data:", err);
            setError(err.message);
        } finally {
            if (!controller.signal.aborted) setLoading(false);
        }
    }, [startDate, endDate, selectedLawType, selectedAgency, selectedMinistry]);

    useEffect(() => {
        fetchDashboardData();
        return () => activeRequest.current?.abort();
    }, [fetchDashboardData]);

    useEffect(() => {
        setSurveyDetailFilter(null);
    }, [startDate, endDate, selectedLawType, selectedAgency, selectedMinistry]);

    useEffect(() => {
        getLawHierarchyOption();
        getFilterOption();
    }, []);

    const getFilterOption = async () => {
        const filter = await ApiMaster.homeFilter();
        if (filter.status == 200) {
            const { data } = filter.data;
            setAgencyOption([...agencyOption, ...data.agency]);
            setMinistryOption([...ministryOption, ...data.ministry]);
        }
    };

    const getLawHierarchyOption = async () => {
        const result = await ApiLaw.getMaster();
        if (result.status == 200) {
            const { data } = result;
            const hierarchy = Array.isArray(data?.hirachy) ? data.hirachy : [];
            setLawHierarchyOption([
                { value: 0, label: "ทุกศักดิ์กฎหมาย" },
                ...hierarchy,
            ]);
        }
    };

    const rangeLabel = useMemo(() => {
        if (!startDate || !endDate) {
            return "ทุกช่วงเวลา";
        }
        return `${moment(startDate).format("DD/MM/YYYY")} - ${moment(endDate).format("DD/MM/YYYY")}`;
    }, [startDate, endDate]);

    const rowsForDonut = agencyListeningStartEnd;

    const donutData = useMemo(() => {
        const totals = new Map();
        rowsForDonut.forEach((row) => {
            const key = row.law_hierarchy_name || "ไม่ระบุ";
            const value = Number(row.total_agencies);
            if (!Number.isNaN(value)) {
                const order = normalizeLawHierarchyOrder(row.order_id);
                const current = totals.get(key);
                totals.set(key, {
                    label: key,
                    value: (current?.value || 0) + value,
                    order: current ? Math.min(current.order, order) : order,
                });
            }
        });
        return Array.from(totals.values()).sort(compareLawHierarchy);
    }, [rowsForDonut]);

    const donutLabels = useMemo(
        () => (donutData.length ? donutData.map((item) => item.label) : ["ไม่มีข้อมูล"]),
        [donutData]
    );
    const donutValues = useMemo(
        () => (donutData.length ? donutData.map((item) => item.value) : [0]),
        [donutData]
    );
    const donutTotal = useMemo(
        () => donutValues.reduce((sum, value) => sum + value, 0),
        [donutValues]
    );
    const donutSeries = useMemo(() => {
        if (!donutData.length) {
            return [0];
        }
        if (!donutTotal) {
            return donutValues.map(() => 0);
        }
        return donutValues;
    }, [donutData.length, donutValues, donutTotal]);

    const baseDonutColors = ["#2563eb", "#f97316", "#10b981", "#ec4899", "#a855f7", "#facc15", "#64748b"];
    const donutColors = useMemo(
        () => (donutData.length ? donutLabels.map((_, idx) => baseDonutColors[idx % baseDonutColors.length]) : ["#e2e8f0"]),
        [donutData.length, donutLabels]
    );

    const donutOptions = useMemo(
        () => ({
            chart: {
                type: "donut",
                toolbar: { show: false },
                fontFamily: "Prompt, 'Noto Sans Thai', sans-serif",
            },
            labels: donutLabels,
            colors: donutColors,
            dataLabels: { enabled: false },
            legend: { show: false },
            tooltip: {
                y: {
                    formatter: (value) => `${formatNumber(value)} ฉบับ`,
                },
            },
            plotOptions: {
                pie: {
                    donut: {
                        size: "70%",
                        labels: {
                            show: true,
                            value: {
                                fontSize: "20px",
                                fontWeight: 600,
                                formatter: (value) => formatNumber(value),
                            },
                            total: {
                                show: true,
                                label: "รวมทั้งหมด",
                                fontSize: "12px",
                                color: "#94a3b8",
                                formatter: () => formatNumber(donutTotal),
                            },
                        },
                    },
                },
            },
        }),
        [donutLabels, donutColors, donutTotal]
    );


    const lawTypesForTable = useMemo(() => {
        const orderByLawType = new Map();

        agencyListeningStartEnd.forEach((row) => {
            const type = row.law_hierarchy_name || "ไม่ระบุ";
            const normalizedOrder = normalizeLawHierarchyOrder(row.order_id);
            const currentOrder = orderByLawType.get(type);

            if (currentOrder === undefined || normalizedOrder < currentOrder) {
                orderByLawType.set(type, normalizedOrder);
            }
        });

        return Array.from(orderByLawType.keys()).sort((a, b) => {
            return compareLawHierarchy(
                { label: a, order: orderByLawType.get(a) },
                { label: b, order: orderByLawType.get(b) }
            );
        });
    }, [agencyListeningStartEnd]);

    const monthColumns = useMemo(() => {
        const columns = new Map();
        agencyListeningStartEnd.forEach((row) => {
            const year = Number(row.year_active);
            const month = Number(row.month_active);
            const definition = monthDefinitions.find((item) => item.value === month);
            if (!Number.isInteger(year) || year <= 0 || !definition || !(Number(row.total_agencies) > 0)) {
                return;
            }
            const key = getMonthKey(year, month);
            if (!columns.has(key)) {
                columns.set(key, {
                    key,
                    year,
                    month,
                    label: `${definition.short} ${year + 543}`,
                    longLabel: `${definition.long} ${year + 543}`,
                });
            }
        });
        return Array.from(columns.values()).sort((a, b) => a.year - b.year || a.month - b.month);
    }, [agencyListeningStartEnd]);

    const tableData = useMemo(() => {
        return lawTypesForTable.map((type) => {
            const typeRows = agencyListeningStartEnd.filter(
                (row) => (row.law_hierarchy_name || "ไม่ระบุ") === type
            );
            const months = {};
            monthColumns.forEach((column) => {
                months[column.key] = 0;
            });
            typeRows.forEach((row) => {
                const key = getMonthKey(row.year_active, row.month_active);
                const value = Number(row.total_agencies);
                if (months[key] !== undefined && Number.isFinite(value)) {
                    months[key] += value;
                }
            });
            const total = typeRows.reduce((sum, row) => {
                const value = Number(row.total_agencies);
                return sum + (Number.isNaN(value) ? 0 : value);
            }, 0);
            return {
                type,
                total,
                months,
            };
        });
    }, [lawTypesForTable, agencyListeningStartEnd, monthColumns]);

    const selectedMinistryLabel = useMemo(() => {
        if (!selectedMinistry) {
            return "ทุกกระทรวง";
        }
        return ministryOption.find((option) => option.value == selectedMinistry)?.label || "ทุกกระทรวง";
    }, [ministryOption, selectedMinistry]);

    const selectedAgencyLabel = useMemo(() => {
        if (!selectedAgency) {
            return "ทุกหน่วยงาน";
        }
        return agencyOption.find((option) => option.value == selectedAgency)?.label || "ทุกหน่วยงาน";
    }, [agencyOption, selectedAgency]);


    const selectedLawLabel = useMemo(() => {
        return lawHierarchyOption.find((option) => option.value == selectedLawType)?.label || "ทุกศักดิ์กฎหมาย";
    }, [lawHierarchyOption, selectedLawType]);

    const showSurveyDetails = (row, column = null) => {
        setSurveyDetailFilter({
            start_date: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
            end_date: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
            lawType: selectedLawType ?? 0,
            agency_id: selectedAgency ?? 0,
            ministry_id: selectedMinistry ?? 0,
            dashboard_type: "listening",
            law_hierarchy_name: row.type,
            year: column?.year || 0,
            month: column?.month || 0,
            month_label: column?.longLabel || "",
            range_label: rangeLabel,
        });
    };


    const handleDownloadExcel = () => downloadExcel([
        ["สถิติการรับฟังร่างกฎหมาย แยกตามศักดิ์กฎหมาย"],
        ["ช่วงวันที่", rangeLabel], ["กระทรวง", selectedMinistryLabel], ["หน่วยงาน", selectedAgencyLabel],
        ["ศักดิ์กฎหมาย", selectedLawLabel],
        ["ศักดิ์กฎหมาย", "รวม", ...monthColumns.map(column => column.longLabel)],
        ...tableData.map(row => [row.type, row.total, ...monthColumns.map(column => row.months[column.key] || 0)]),
    ], `draftlaw-dashboard-${moment(startDate).format("YYYYMMDD")}-${moment(endDate).format("YYYYMMDD")}`);

    if (loading) return <DashboardSkeleton variant="draftlaw" />;
    if (error) {
        return (
            <div className="min-h-screen bg-[#eef2f7] px-6 pb-20 pt-24">
                <div role="alert" className="mx-auto max-w-7xl rounded-2xl bg-white p-10 text-center">
                    <p className="text-slate-600">ไม่สามารถโหลดข้อมูลได้ กรุณาลองใหม่อีกครั้ง</p>
                    <button onClick={fetchDashboardData} className="mt-4 rounded-lg bg-sky-600 px-4 py-2 text-white">ลองใหม่อีกครั้ง</button>
                </div>
            </div>
        );
    }
    return (
        <div className="min-h-screen bg-[#eef2f7] pb-20 pt-24">
            <div className="mx-auto max-w-7xl space-y-5 px-4 sm:px-6 lg:px-8">
                <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7">
                    <div className="flex flex-wrap items-center gap-3">
                        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-[#2367b1]">
                            <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
                                <path d="M4 17l5-5 3 3 7-8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M15 7h4v4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </span>
                        <div className="flex min-w-0 flex-1 items-center gap-3">
                            <button
                                onClick={() => history.go(-1)}
                                className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-medium text-sky-600 transition hover:border-sky-200 hover:bg-sky-50"
                            >
                                <svg viewBox="0 0 24 24" className="h-4 w-4">
                                    <path
                                        d="M15 18l-6-6 6-6"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                ย้อนกลับ
                            </button>
                            <h1 className="truncate text-sm font-bold text-[#2367b1] sm:text-base">
                                สถิติการรับฟังร่างกฎหมาย แยกตามศักดิ์กฎหมาย
                            </h1>
                            <button type="button" onClick={handleDownloadExcel} className="ml-auto whitespace-nowrap rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">ดาวน์โหลด Excel</button>
                        </div>
                    </div>
                    <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
                        <DateFilterInput
                            label="วันที่เริ่มต้น"
                            value={startDate}
                            onChange={setStartDate}
                        />
                        <DateFilterInput
                            label="วันที่สิ้นสุด"
                            value={endDate}
                            onChange={setEndDate}
                        />
                        <label className="flex flex-col gap-1 text-xs text-slate-500">
                            ศักดิ์กฎหมาย
                            <Select
                                className="basic-single"
                                classNamePrefix="select"
                                defaultValue={lawHierarchyOption[0]}
                                options={lawHierarchyOption}
                                value={lawHierarchyOption.find((x) => x.value == selectedLawType)}
                                onChange={(item) => {
                                    setSelectedLawType(item.value);
                                }}
                            />
                        </label>

                        <label className="flex flex-col gap-1 text-xs text-slate-500">
                            กระทรวง
                            <Select
                                className="basic-single"
                                classNamePrefix="select"
                                defaultValue={ministryOption[0]}
                                value={ministryOption.find((x) => x.value == selectedMinistry)}
                                options={ministryOption}
                                onChange={(item) => {
                                    setSelectedMinistry(item.value);
                                    setSelectedAgency(0);
                                }}
                            />
                        </label>
                        <label className="flex flex-col gap-1 text-xs text-slate-500 md:col-span-2 xl:col-span-1">
                            หน่วยงาน
                            <Select
                                className="basic-single"
                                classNamePrefix="select"
                                defaultValue={agencyOption[0]}
                                isSearchable
                                aria-label="ค้นหาหน่วยงาน"
                                noOptionsMessage={() => "ไม่พบหน่วยงาน"}
                                options={
                                    selectedMinistry > 0
                                        ? agencyOption.filter((x) => x.value === 0 || x.ministry_id == selectedMinistry)
                                        : agencyOption
                                }
                                value={agencyOption.find((x) => x.value == selectedAgency)}
                                onChange={(item) => {
                                    setSelectedAgency(item.value);
                                }}
                            />
                        </label>
                    </div>
                </section>


                <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="flex flex-col gap-3 border-b border-dashed border-slate-200 px-5 py-5 sm:px-7 lg:flex-row lg:items-center lg:justify-between">
                        <h2 className="text-sm font-semibold text-slate-800">การรับฟังแยกตามประเภทศักดิ์กฎหมาย</h2>
                        <div className="flex items-start gap-2 text-xs text-slate-500">
                            <img
                                alt=""
                                className="mt-0.5 h-4 w-4"
                                src={StaticImage("/images/icon_v3/blue/calendar-lines-duotone.svg")}
                            />
                            <p>
                                <span className="font-semibold text-[#2367b1]">ผลลัพธ์การกรองข้อมูล:</span>{" "}
                                <span className="font-medium text-slate-700">
                                    {rangeLabel} {selectedLawLabel} {selectedMinistryLabel} {selectedAgencyLabel}
                                </span>
                            </p>
                        </div>
                    </div>
                    <div className="px-5 py-6 sm:px-7">
                        <div className="grid items-center gap-7 lg:grid-cols-[220px_minmax(0,1fr)]">
                            <div className="mx-auto lg:mx-0">
                                <ReactApexChart options={donutOptions} series={donutSeries} type="donut" width={210} />
                            </div>
                            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                                {donutLabels.map((label, idx) => {
                                    const percentage = donutTotal
                                        ? Number(((donutValues[idx] / donutTotal) * 100).toFixed(2))
                                        : 0;
                                    return (
                                        <div key={label} className="flex min-h-[72px] min-w-0 flex-col gap-2 rounded-xl bg-slate-50 px-3 py-2.5">
                                            <div className="flex min-w-0 flex-1 items-start gap-2 overflow-hidden">
                                                <span
                                                    className="mt-0.5 h-3 w-3 shrink-0 rounded-full"
                                                    style={{ backgroundColor: donutColors[idx] }}
                                                />
                                                <span className="min-w-0 break-all text-[11px] font-medium leading-4 text-slate-600">{label}</span>
                                            </div>
                                            <div className="flex w-full items-center justify-between gap-1">
                                                <span className="text-sm font-bold text-slate-800">{formatNumber(donutValues[idx])}</span>
                                                <span className="rounded-md bg-white px-1.5 py-1 text-[11px] text-slate-600">{percentage}%</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="mt-6 flex items-start gap-3 rounded-xl border border-dashed border-rose-300 bg-rose-50 px-4 py-3.5 text-xs text-rose-700">
                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-rose-100 text-rose-500">
                                <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
                                    <path d="M5 6h14M5 11h9M5 16h6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                            </span>
                            <p className="leading-6">
                                <span className="font-bold">หมายเหตุ:</span> ค่า "ไม่ระบุ" หมายถึง ข้อมูลการรับฟังร่างกฎหมายลำดับรองจากชุดข้อมูลเดิม ซึ่งยังไม่มีการแยกลำดับศักดิ์กฎหมาย จึงไม่สามารถระบุลำดับศักดิ์ได้
                            </p>
                        </div>
                    </div>
                </section>

                <section className="pt-4">
                    <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                        <div>
                            <p className="text-sm font-bold text-[#2367b1]">ข้อมูลสถิติแยกตามศักดิ์กฎหมาย</p>
                            <p className="mt-1 text-xs text-slate-500">
                                ผลลัพธ์การกรองข้อมูล {rangeLabel} {selectedLawLabel} {selectedMinistryLabel} {selectedAgencyLabel}
                            </p>
                        </div>
                        <p className="text-sm text-slate-600">
                            ยอดรวม <span className="text-lg font-bold text-slate-900">{formatNumber(_.sumBy(tableData, x => Number(x.total)))}</span> ฉบับ
                        </p>
                    </div>
                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                        <div className="overflow-x-auto">
                            <table className="min-w-[1100px] w-full text-sm">
                                <thead className="bg-slate-50 text-xs font-semibold text-slate-600">
                                    <tr>
                                        <th className="sticky left-0 z-20 min-w-[190px] border-r border-slate-200 bg-slate-50 px-5 py-4 text-left">ศักดิ์กฎหมาย</th>
                                        <th className="sticky left-[190px] z-20 min-w-[170px] border-r border-slate-200 bg-blue-50 px-4 py-4 text-center">ยอดสะสมทั้งหมด (ฉบับ)</th>
                                        {monthColumns.map((column) => (
                                            <th key={column.key} className="min-w-[105px] whitespace-nowrap px-3 py-4 text-center">{column.label}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {tableData.map((row, rowIndex) => (
                                        <tr key={row.type} className="border-t border-slate-100 text-slate-700 transition hover:bg-blue-50/40">
                                            <td className={`sticky left-0 z-10 border-r border-slate-100 px-5 py-4 font-medium ${rowIndex % 2 === 0 ? "bg-slate-50" : "bg-white"}`}>
                                                {row.type}
                                            </td>
                                            <td className="sticky left-[190px] z-10 border-r border-slate-100 bg-blue-50 px-4 py-4 text-center font-semibold text-slate-900">
                                                <button
                                                    type="button"
                                                    onClick={() => showSurveyDetails(row)}
                                                    className="font-semibold text-sky-600 underline decoration-sky-300 underline-offset-2 hover:text-sky-800"
                                                    aria-label={`ดูรายละเอียด ${row.type} ยอดสะสม ${formatNumber(row.total)} รายการ`}
                                                >
                                                    {formatNumber(row.total)}
                                                </button>
                                            </td>
                                            {monthColumns.map((column) => {
                                                const monthValue = row.months[column.key];
                                                return (
                                                    <td key={`${row.type}-${column.key}`} className={`px-3 py-4 text-center text-slate-500 ${rowIndex % 2 === 0 ? "bg-slate-50/70" : "bg-white"}`}>
                                                        {monthValue > 0 ? (
                                                            <button
                                                                type="button"
                                                                onClick={() => showSurveyDetails(row, column)}
                                                                className="font-medium text-sky-600 underline decoration-sky-300 underline-offset-2 hover:text-sky-800"
                                                                aria-label={`ดูรายละเอียด ${row.type} เดือน${column.longLabel} ${formatNumber(monthValue)} รายการ`}
                                                            >
                                                                {formatNumber(monthValue)}
                                                            </button>
                                                        ) : "0"}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                    {!tableData.length && (
                                        <tr>
                                            <td colSpan={monthColumns.length + 2} className="px-6 py-12 text-center text-sm text-slate-400">
                                                ไม่มีข้อมูลในช่วงเวลาที่เลือก
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </section>
                {surveyDetailFilter && (
                    <SurveyDetailTable
                        filter={surveyDetailFilter}
                        onClose={() => setSurveyDetailFilter(null)}
                    />
                )}
            </div>
        </div>
    );
};

export default DraftlawDashboard;
